import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Shield, Smartphone, Key, CheckCircle, XCircle } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { twoFactorService, TwoFactorSetupResponse } from '@/services/twoFactorService';

interface TwoFactorSetupProps {
  isEnabled: boolean;
  onStatusChange: (enabled: boolean) => void;
}

export const TwoFactorSetup = ({ isEnabled, onStatusChange }: TwoFactorSetupProps) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [setupData, setSetupData] = useState<TwoFactorSetupResponse | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [step, setStep] = useState<'initial' | 'setup' | 'verify'>('initial');

  const handleSetup = async () => {
    setError('');
    setLoading(true);

    try {
      const data = await twoFactorService.setup();
      setSetupData(data);
      setStep('setup');
    } catch (err: unknown) {
      let errorMessage = 'Error al configurar 2FA';
      if (err && typeof err === 'object' && 'response' in err && 
          err.response && typeof err.response === 'object' && 'data' in err.response &&
          err.response.data && typeof err.response.data === 'object' && 'message' in err.response.data) {
        errorMessage = String(err.response.data.message);
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!verificationCode) {
      setError('Por favor ingresa el código de verificación');
      return;
    }

    setError('');
    setLoading(true);

    try {
      await twoFactorService.verify(verificationCode);
      toast({
        title: "¡2FA Habilitado!",
        description: "La autenticación de dos factores ha sido configurada exitosamente.",
      });
      onStatusChange(true);
      setStep('initial');
      setSetupData(null);
      setVerificationCode('');
    } catch (err: unknown) {
      let errorMessage = 'Código de verificación inválido';
      if (err && typeof err === 'object' && 'response' in err && 
          err.response && typeof err.response === 'object' && 'data' in err.response &&
          err.response.data && typeof err.response.data === 'object' && 'message' in err.response.data) {
        errorMessage = String(err.response.data.message);
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    if (!verificationCode) {
      setError('Por favor ingresa el código de verificación para deshabilitar 2FA');
      return;
    }

    setError('');
    setLoading(true);

    try {
      await twoFactorService.disable(verificationCode);
      toast({
        title: "2FA Deshabilitado",
        description: "La autenticación de dos factores ha sido deshabilitada.",
      });
      onStatusChange(false);
      setVerificationCode('');
    } catch (err: unknown) {
      let errorMessage = 'Error al deshabilitar 2FA';
      if (err && typeof err === 'object' && 'response' in err && 
          err.response && typeof err.response === 'object' && 'data' in err.response &&
          err.response.data && typeof err.response.data === 'object' && 'message' in err.response.data) {
        errorMessage = String(err.response.data.message);
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (step === 'setup' && setupData) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Smartphone className="h-5 w-5" />
            Configurar Autenticador
          </CardTitle>
          <CardDescription>
            Escanea el código QR con tu app de autenticación
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="text-center">
            <img src={setupData.qrCode} alt="QR Code" className="mx-auto border rounded-lg" />
          </div>

          <div className="space-y-2">
            <Label>Clave manual (si no puedes escanear el QR):</Label>
            <div className="p-3 bg-peach/10 border border-primary/20 rounded-md font-mono text-sm break-all text-brown">
              {setupData.manualEntryKey}
            </div>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="verification-code">Código de verificación:</Label>
              <Input
                id="verification-code"
                type="text"
                placeholder="123456"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                maxLength={6}
                className="text-center text-lg font-mono bg-peach/20 border-2 border-primary/30 focus:border-primary focus:bg-white transition-all duration-200 tracking-wider"
              />
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="flex gap-2">
              <Button
                onClick={() => {
                  setStep('initial');
                  setSetupData(null);
                  setVerificationCode('');
                  setError('');
                }}
                variant="outline"
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleVerify}
                disabled={loading || !verificationCode}
                className="flex-1"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verificando...
                  </>
                ) : (
                  'Verificar y Habilitar'
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5" />
          Autenticación de Dos Factores (2FA)
        </CardTitle>
        <CardDescription>
          Agrega una capa extra de seguridad a tu cuenta
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-3 p-4 border rounded-lg">
          {isEnabled ? (
            <>
              <CheckCircle className="h-5 w-5 text-green-500" />
              <div>
                <p className="font-medium text-green-700">2FA Habilitado</p>
                <p className="text-sm text-muted-foreground">Tu cuenta está protegida</p>
              </div>
            </>
          ) : (
            <>
              <XCircle className="h-5 w-5 text-orange-500" />
              <div>
                <p className="font-medium text-orange-700">2FA Deshabilitado</p>
                <p className="text-sm text-muted-foreground">Recomendamos habilitar 2FA para mayor seguridad</p>
              </div>
            </>
          )}
        </div>

        {!isEnabled ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <h4 className="font-medium">¿Qué necesitas?</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Google Authenticator, Microsoft Authenticator u otra app compatible</li>
                <li>• Acceso a tu dispositivo móvil</li>
              </ul>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Button onClick={handleSetup} disabled={loading} className="w-full">
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Configurando...
                </>
              ) : (
                <>
                  <Key className="mr-2 h-4 w-4" />
                  Configurar 2FA
                </>
              )}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="disable-code">Código de verificación para deshabilitar:</Label>
              <Input
                id="disable-code"
                type="text"
                placeholder="123456"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                maxLength={6}
                className="text-center text-lg font-mono bg-peach/20 border-2 border-primary/30 focus:border-primary focus:bg-white transition-all duration-200 tracking-wider"
              />
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Button
              onClick={handleDisable}
              disabled={loading || !verificationCode}
              variant="destructive"
              className="w-full"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deshabilitando...
                </>
              ) : (
                'Deshabilitar 2FA'
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};